import { useState } from "react";
import styles from "./identity-control.module.css";

/**
 * A self-contained, CSS-Modules-only 40x40 avatar (design.md's "square
 * avatar" convention: 40x40, 6px radius, bordered) for app-shell.
 *
 * This does NOT reuse `<AvatarV2>` from `components/avatar` — that
 * component is styled entirely with Tailwind utility classes (`size-10
 * rounded-xs border-gray-300 bg-white …`), which only resolve to real CSS
 * inside apps that run this repo's Tailwind pipeline. app-shell consumers
 * (like the new CSS-Modules-only `platform` app) don't, so `<AvatarV2>`
 * silently rendered as an unstyled, unbordered, uncentered box there — only
 * its one inline style (the initials' background color) survived. Kept
 * self-contained here rather than "fixing" `<AvatarV2>` itself, since that
 * component is shared by the existing Tailwind-based app and out of scope
 * to touch.
 */
export function RailAvatar({
  name,
  imageUrl,
}: {
  name: string;
  imageUrl?: string | null;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imageFailed;
  const initials = getInitials(name);

  return (
    <span className={styles.avatar} aria-hidden="true">
      {showImage ? (
        <img
          className={styles.avatarImage}
          src={imageUrl!}
          alt=""
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span
          className={styles.avatarFallback}
          style={{ backgroundColor: getColorFromInitials(initials) }}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

function getInitials(name: string): string {
  const chunks = (name ?? "").replace(/['"]/gu, "").trim().split(/\s+/);
  let initials = "";
  for (let i = 0; i < chunks.length && initials.length < 2; i++) {
    const chars = Array.from(chunks[i]);
    if (chars[0]) initials += chars[0];
  }
  return initials.toUpperCase();
}

// Same hash-to-pastel-hue approach as components/avatar's getColorFromInitials
// (kept local rather than imported since that one isn't exported).
function getColorFromInitials(initials: string): string {
  if (!initials) return "var(--card-2)";
  const chars = initials.slice(0, 2).padEnd(2, initials[0] || "A");
  const hash = (chars.charCodeAt(0) * 31 + chars.charCodeAt(1) * 17) % 360;
  return `hsl(${Math.abs(hash)}, 40%, 90%)`;
}
